// @ts-nocheck — migrated from JS; tighten types in a follow-up
import React, { useEffect, useRef, useState } from 'react';
import './Congratulations.scss';
import '../emailVerification/Email.scss';
import { useTranslation } from 'react-i18next';
import { getAccount, getPartner } from '../../utils/storage';
import RegistrationPage from '../../components/layouts/RegistrationPage';
import { navigateToPartnerRedirect } from '../../utils/safeRedirect';
import {
    appendPartnerInfoSearchParams,
    resolvePartnerInfo,
} from '../../utils/partnerRedirectFlow';
import PageHeader from '../../components/PageHeader';
import successIcon from '../../assets/svgs/imid-verification-success.svg';
import copyIcon from '../../assets/svgs/copy-icon.svg';
import bankingQr from '../../assets/images/mobile-banking-qr.svg';
import CustomButton from '../../components/customButton/CustomButton';
import ActionDialog from '../../components/dialog/ActionDialog';
import { isMobileDevice } from '../../utils/isMobileDevice';
import { getContinuationLink } from '../../utils/getContinuationLink';
import { cn } from '../../utils/cn';
import { AnalyticsEvent, useAnalytics } from '../../analytics';
import {
    durationSecSince,
    getAnalyticsVerificationMethod,
    getRegistrationStartedAt,
    markRegistrationCompleted,
} from '../../analytics';

const EMPTY_ACCOUNT = {};

async function copyToClipboard(text) {
    if (!text) return false;

    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        try {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.select();
            const copied = document.execCommand('copy');
            document.body.removeChild(textarea);
            return copied;
        } catch {
            return false;
        }
    }
}

function CopyButton({ value, label }) {
    const handleCopy = () => {
        void copyToClipboard(value);
    };

    return (
        <button
            type="button"
            onClick={handleCopy}
            aria-label={label}
            className="ml-1.5 inline-flex shrink-0 items-center justify-center hover:opacity-70"
        >
            <img src={copyIcon} alt="" className="h-5 w-5" />
        </button>
    );
}

function DetailRow({ label, value, copyValue }) {
    return (
        <div className="flex items-center justify-between gap-1 flex-wrap">
            <span className="text-sm !leading-none text-custom-gray-800/80">{label}</span>
            <div className="flex min-w-0 items-center">
                <span className="text-sm font-medium !leading-relaxed">{value}</span>
                {copyValue ? <CopyButton value={copyValue} label={label} /> : null}
            </div>
        </div>
    );
}

export default function Congratulations() {
    const { t } = useTranslation();
    const { track } = useAnalytics();
    const trackedCompletionRef = useRef(false);
    const [isOpenContinuation, setIsOpenContinuation] = useState(false);
    const rawAccount = getAccount();
    const account =
        typeof rawAccount === 'object' && rawAccount !== null ? rawAccount : EMPTY_ACCOUNT;
    const partner = getPartner();
    const isMobile = isMobileDevice();

    useEffect(() => {
        if (trackedCompletionRef.current) {
            return;
        }
        const sent = track(AnalyticsEvent.REGISTRATION_COMPLETED, {
            verification_method: getAnalyticsVerificationMethod() ?? null,
            duration_sec: durationSecSince(getRegistrationStartedAt()) ?? 0,
        });
        if (sent) {
            trackedCompletionRef.current = true;
            markRegistrationCompleted();
        }
    }, [track]);

    useEffect(() => {
        let href;
        if (account?.partnerRedirectUrl) {
            href = account.partnerRedirectUrl;
        } else if (partner?.partnerRedirectUrl) {
            href = `${partner.partnerRedirectUrl}?success=true`;
        } else {
            return undefined;
        }

        const partnerInfo = resolvePartnerInfo(account, partner);
        href = appendPartnerInfoSearchParams(href, partnerInfo);

        const id = setTimeout(() => {
            navigateToPartnerRedirect(href);
        }, 5000);
        return () => clearTimeout(id);
    }, [account, partner]);

    const openInstructionModal = () => setIsOpenContinuation(true);

    const closeInstructionModal = () => setIsOpenContinuation(false);

    return (
        <RegistrationPage
            contentClassName="w-full md:max-w-[32.5rem] mx-auto gap-8 h-full md:h-auto"
            stickyFooter
            footer={
                <>
                    <CustomButton
                        type="button"
                        className="w-full md:max-w-fit mx-auto text-center"
                        onClick={openInstructionModal}
                    >
                        {t(
                            isMobile
                                ? 'congratulationsToMobileApp'
                                : 'congratulationsToInternetBanking'
                        )}
                    </CustomButton>
                </>
            }
        >
            <PageHeader title={t('congtitle')} imgSrc={successIcon} />
            <div className="flex flex-col gap-6 h-full md:h-auto">
                <div className="flex flex-col gap-2">
                    <div className="w-full rounded-lg border border-custom-border-card bg-white p-4 flex flex-col gap-2">
                        <DetailRow
                            label={t('congratulationsAccountName')}
                            value={account.username}
                            copyValue={account.username}
                        />
                        {!account?.hadAccount && (
                            <>
                                <DetailRow
                                    label={t('congratulationsAccount')}
                                    value={account.account}
                                    copyValue={account.account?.toString()}
                                />
                                <DetailRow label={t('congratulationsCurrency')} value="AMD" />
                            </>
                        )}
                    </div>
                    {!account?.hadAccount && (
                        <p className="my-2 text-xs leading-relaxed text-secondary">
                            {t('congratulationsAgreement')}
                        </p>
                    )}
                </div>
                <div
                    className={cn(
                        'flex w-full md:max-w-[32.5rem] mx-auto flex-col gap-8 mt-auto md:mt-0',
                        isMobile && 'hidden'
                    )}
                >
                    <div className="flex items-center w-full bg-primary/5 rounded-lg py-4 px-11 gap-4">
                        <img
                            src={bankingQr}
                            alt={t('congratulationsMobileBankingQrAlt')}
                            width={102}
                            height={102}
                        />
                        <div className="flex flex-col gap-1">
                            <span className="text-sm font-semibold text-custom-gray-600">
                                {t('congratulationsContinueOnPhone')}
                            </span>
                            <span className="text-xs text-custom-gray-600">
                                {t('congratulationsScanQr')}
                            </span>
                        </div>
                    </div>
                    <div className="w-full flex items-center gap-6 mb-6">
                        <div
                            className="border w-full"
                            style={{
                                borderImageSource:
                                    'linear-gradient(90deg, rgba(221, 221, 223, 0) 0%, #DDDDDF 99.99%)',
                                borderImageSlice: 1,
                            }}
                        />
                        <span className="text-custom-gray-300 text-sm">{t('commonOr')}</span>
                        <div
                            className="border w-full"
                            style={{
                                borderImageSource:
                                    'linear-gradient(90deg, #DDDDDF 0.01%, rgba(221, 221, 223, 0) 100%)',
                                borderImageSlice: 1,
                            }}
                        />
                    </div>
                </div>
            </div>
            <ActionDialog
                open={isOpenContinuation}
                onCancel={closeInstructionModal}
                onConfirm={closeInstructionModal}
                closeLabel={t('dialogClose')}
                title={t('congratulationsMobileBankingModalTitle')}
                description={
                    <>
                        <p>{t('congratulationsMobileBankingModalText')}</p>
                        <p className="mt-4">{t('congratulationsMobileBankingModalNote')}</p>
                    </>
                }
                confirmButtonText={t('errorButton')}
                confirmHref={getContinuationLink()}
            />
        </RegistrationPage>
    );
}
